import { network } from './network'
import { i18n } from './i18n'
import {
  invalidateSessionToken,
  recordAuthenticatedActivity,
  session,
} from './session'
import { measureUxOnce } from './metrics'
import { announceGameError, gameErrorMessage } from './gameError'
import { uiText } from './ui'
import {
  cachedReadModel,
  currentReadModelGeneration,
  invalidateReadModels,
  pendingReadModel,
  rememberReadModel,
  trackReadModel,
} from './readModelCache'
import type {
  ApiProblem,
  JsonObject,
  LoginResponse,
  RegistrationRequirements,
  RegistrationResponse,
  UxBootstrap,
} from './types'

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly problem: ApiProblem,
  ) {
    super(gameErrorMessage(problem))
  }
}

export class OfflineCommandError extends Error {
  public constructor() {
    super(
      uiText(
        'Du bist offline. Spielaktionen können erst nach Wiederherstellung der Verbindung ausgeführt werden.',
        'You are offline. Game actions can only be performed after reconnecting.',
      ),
    )
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: JsonObject
  idempotency?: boolean
  anonymous?: boolean
  signal?: AbortSignal
  cache?: 'default' | 'reload' | 'no-store'
  readOnly?: boolean
}

function correlationId(): string {
  return crypto.randomUUID()
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (text === '') return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    // Never surface reverse-proxy/Symfony HTML or arbitrary plaintext as a
    // player-facing error detail. The status and correlation id are sufficient
    // for a safe, actionable error notice.
    return { code: 'INVALID_SERVER_RESPONSE' }
  }
}

export function invalidateReadCache(): void {
  invalidateReadModels()
}

export function invalidateVillageReadCache(
  worldId: string,
  villageId: string,
): void {
  const needle = `/api/v2/worlds/${worldId}/villages/${villageId}`
  invalidateReadModels((key) => key.includes(needle))
}

function readCacheKey(path: string): string {
  return `${session.token ?? 'anonymous'}|${i18n.locale}|${path}`
}

function isReusableReadPath(path: string): boolean {
  return [
    /^\/api\/v2\/content\/catalog(?:\?|$)/,
    /^\/api\/v2\/notifications(?:\?|$)/,
    /^\/api\/v2\/worlds\/[^/]+\/ui-catalog$/,
    /^\/api\/v2\/worlds\/[^/]+\/dashboard$/,
    /^\/api\/v2\/worlds\/[^/]+\/movement-center$/,
    /^\/api\/v2\/worlds\/[^/]+\/map\/chunks\/-?\d+\/-?\d+$/,
    /^\/api\/v2\/worlds\/[^/]+\/report-center(?:\?|$)/,
    /^\/api\/v2\/worlds\/[^/]+\/messages\?limit=/,
    /^\/api\/v2\/worlds\/[^/]+\/alliances\/me$/,
    /^\/api\/v2\/worlds\/[^/]+\/alliance-content$/,
    /^\/api\/v2\/worlds\/[^/]+\/alliances\/[^/]+\/calendar$/,
    /^\/api\/v2\/worlds\/[^/]+\/content\/(?:journal|inventory)$/,
    /^\/api\/v2\/worlds\/[^/]+\/rankings\/[^?]+(?:\?|$)/,
    /^\/api\/v2\/worlds\/[^/]+\/victory(?:\/result)?$/,
    /^\/api\/v2\/worlds\/[^/]+\/chronicle(?:\?|$)/,
    // Mutable village gameplay projections are deliberately not reused across
    // navigations. Queue completions can change action eligibility between two
    // adjacent views; serving a 10s-old value here can otherwise combine a fresh
    // empty queue with stale "queue full" blockers. Live-sync already controls
    // when these endpoints are fetched, so correctness does not require polling.
  ].some((pattern) => pattern.test(path))
}

function isMutableVillageGameplayReadPath(path: string): boolean {
  return /^\/api\/v2\/worlds\/[^/]+\/villages\/[^/]+(?:\/|(?:\?|$))/.test(path)
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET'
  const readOnlyPost =
    method === 'POST' &&
    options.readOnly === true &&
    (/^\/api\/v2\/worlds\/[^/]+\/villages\/[^/]+\/attack-preview$/.test(path) ||
      path === '/api/v2/realtime/authorization')
  const mutating = method !== 'GET' && !readOnlyPost
  if (mutating && (!navigator.onLine || network.state === 'offline')) {
    announceGameError({ code: 'OFFLINE_COMMAND_BLOCKED' })
    throw new OfflineCommandError()
  }

  const sharedRead =
    method === 'GET' &&
    options.signal === undefined &&
    options.cache !== 'no-store' &&
    isReusableReadPath(path)
  const cacheKey = sharedRead ? readCacheKey(path) : ''
  if (sharedRead && options.cache !== 'reload') {
    const cached = cachedReadModel(cacheKey)
    if (cached.hit) {
      measureUxOnce('route-first-read', 'navigation:data')
      return cached.value as T
    }
    const pending = pendingReadModel<T>(cacheKey)
    if (pending) return pending
  }

  const nativeCache =
    options.cache ??
    (method === 'GET' && isMutableVillageGameplayReadPath(path)
      ? 'no-store'
      : undefined)

  const tokenUsed = !options.anonymous ? session.token : null

  const headers = new Headers({
    Accept: 'application/json',
    'Accept-Language': i18n.locale,
    'X-Correlation-ID': correlationId(),
  })
  if (nativeCache === 'no-store') {
    headers.set('Cache-Control', 'no-store')
    headers.set('Pragma', 'no-cache')
  } else if (nativeCache === 'reload') {
    headers.set('Cache-Control', 'no-cache')
    headers.set('Pragma', 'no-cache')
  }
  if (options.body !== undefined)
    headers.set('Content-Type', 'application/json')
  if (tokenUsed) headers.set('Authorization', `Bearer ${tokenUsed}`)
  if (options.idempotency) headers.set('Idempotency-Key', crypto.randomUUID())

  const init: RequestInit = { method, headers, credentials: 'same-origin' }
  if (nativeCache !== undefined) init.cache = nativeCache
  if (options.body !== undefined) init.body = JSON.stringify(options.body)
  if (options.signal !== undefined) init.signal = options.signal
  const execute = async (): Promise<Response> => fetch(path, init)

  const perform = async (): Promise<T> => {
    let response: Response
    try {
      response = await execute()
    } catch {
      try {
        if (method === 'GET' && navigator.onLine) response = await execute()
        else throw new Error('NETWORK_ERROR')
      } catch {
        const problem: ApiProblem = { code: 'NETWORK_ERROR' }
        if (mutating) announceGameError(problem)
        throw new Error(gameErrorMessage(problem))
      }
    }

    const payload = await parseJson(response)
    if (!response.ok) {
      const problem = (
        payload !== null && typeof payload === 'object' ? payload : {}
      ) as Partial<ApiProblem>
      const normalized: ApiProblem = {
        code:
          typeof problem.code === 'string'
            ? problem.code
            : `HTTP_${String(response.status)}`,
      }
      if (typeof problem.title === 'string') normalized.title = problem.title
      if (typeof problem.detail === 'string') normalized.detail = problem.detail
      if (typeof problem.correlationId === 'string')
        normalized.correlationId = problem.correlationId
      else {
        const responseCorrelationId = response.headers.get('X-Correlation-ID')
        if (responseCorrelationId)
          normalized.correlationId = responseCorrelationId
      }
      if (problem.fieldErrors !== undefined)
        normalized.fieldErrors = problem.fieldErrors
      if (Array.isArray(problem.blockers))
        normalized.blockers = problem.blockers
      const sessionInvalid =
        response.status === 401 &&
        tokenUsed !== null &&
        [
          'AUTH_UNAUTHENTICATED',
          'AUTH_REQUIRED',
          'AUTH_TOKEN_INVALID_OR_EXPIRED',
        ].includes(normalized.code)
      if (sessionInvalid) invalidateSessionToken(tokenUsed)
      if ((mutating || response.status >= 500) && !sessionInvalid)
        announceGameError(normalized, response.status)
      throw new ApiError(response.status, normalized)
    }
    if (tokenUsed !== null) recordAuthenticatedActivity(tokenUsed)
    if (mutating) {
      // Commands stay server-authoritative. Any successful mutation invalidates
      // navigation snapshots before subscribers perform their canonical reload.
      invalidateReadCache()
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('ci:mutation-committed'))
        if ('BroadcastChannel' in window) {
          const channel = new BroadcastChannel('craft-imperium-live-v62')
          channel.postMessage({ type: 'mutation-committed' })
          channel.close()
        }
      }
    }
    if (method === 'GET') measureUxOnce('route-first-read', 'navigation:data')
    return payload as T
  }

  if (!sharedRead) return perform()
  const generation = currentReadModelGeneration()
  const promise = perform().then((value) =>
    rememberReadModel(cacheKey, value, generation),
  )
  return trackReadModel(cacheKey, promise)
}

export function login(
  loginName: string,
  password: string,
  totpCode: string,
  rememberMe = false,
): Promise<LoginResponse> {
  const body: JsonObject = {
    login: loginName,
    password,
    rememberMe,
    deviceType: 'WEB_PWA',
  }
  if (totpCode !== '') body.totpCode = totpCode
  return request<LoginResponse>('/api/v2/sessions', {
    method: 'POST',
    body,
    anonymous: true,
  })
}

export function restoreRememberedSession(): Promise<LoginResponse> {
  return request<LoginResponse>('/api/v2/sessions/current', {
    anonymous: true,
    cache: 'no-store',
  })
}

export function keepSessionAlive(): Promise<{ expiresAt: string }> {
  return request<{ expiresAt: string }>('/api/v2/sessions/current/keepalive', {
    cache: 'no-store',
  })
}

export function registrationRequirements(): Promise<RegistrationRequirements> {
  return request<RegistrationRequirements>(
    '/api/v2/registration-requirements',
    {
      anonymous: true,
    },
  )
}

export function registerAccount(input: {
  username: string
  email: string
  password: string
  passwordConfirmation: string
  termsVersion: string
  privacyVersion: string
  minimumAgeConfirmed: boolean
}): Promise<RegistrationResponse> {
  return request<RegistrationResponse>('/api/v2/accounts', {
    method: 'POST',
    body: { ...input },
    anonymous: true,
    idempotency: true,
  })
}

export function verifyEmail(
  token: string,
): Promise<{ accountId: string; emailVerified: true }> {
  return request('/api/v2/email-verifications', {
    method: 'POST',
    body: { token },
    anonymous: true,
  })
}

export function resendEmailVerification(): Promise<{ accepted: true }> {
  return request('/api/v2/email-verification-requests', { method: 'POST' })
}

export function bootstrap(): Promise<UxBootstrap> {
  return request<UxBootstrap>('/api/v2/ux/bootstrap')
}
